/**
 * NotificationContext - Global notification state management
 * 
 * Provides real-time notifications across the application with WebSocket support
 * and persistent storage for notification history.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { isAuthCloseCode, isAuthTicketError, openBatchJobsWs } from '../services/batchJobsWs'

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type NotificationType = 'info' | 'success' | 'warning' | 'error'
export type NotificationCategory = 'batch_job' | 'system' | 'alert' | 'update'

export interface AppNotification {
  id: string
  type: NotificationType
  category: NotificationCategory
  title: string
  message: string
  timestamp: Date
  read: boolean
  actionUrl?: string
  actionLabel?: string
  metadata?: Record<string, any>
}

interface NotificationContextType {
  notifications: AppNotification[]
  unreadCount: number
  isConnected: boolean
  addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  removeNotification: (notificationId: string) => void
  clearAll: () => void
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface NotificationProviderProps {
  children: ReactNode
  maxNotifications?: number
  storybookId?: string
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 50,
  storybookId
}) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [isConnected, setIsConnected] = useState(false)

  // Generate unique ID
  const generateId = () => `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Add notification
  const addNotification = useCallback((notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: AppNotification = {
      ...notification,
      id: generateId(),
      timestamp: new Date(),
      read: false
    }

    setNotifications(prev => {
      const updated = [newNotification, ...prev]
      // Keep only maxNotifications
      return updated.slice(0, maxNotifications)
    })

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/ignis-icon.png',
        tag: newNotification.id
      })
    }
  }, [maxNotifications])

  // Mark as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    )
  }, [])

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  // Remove notification
  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId))
  }, [])

  // Clear all
  const clearAll = useCallback(() => {
    setNotifications([])
  }, [])

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!storybookId) return

    let ws: WebSocket | null = null
    let reconnectTimeout: NodeJS.Timeout | null = null
    let disposed = false

    const scheduleReconnect = () => {
      if (disposed) return
      // Reconnect after 5 seconds
      reconnectTimeout = setTimeout(() => { void connect() }, 5000)
    }

    // Each attempt fetches a fresh single-use ticket; the shared helper also
    // keeps the WS on the same runtime hostname rules as HTTP (#233, #234, #3102)
    const connect = async () => {
      let socket: WebSocket
      try {
        socket = await openBatchJobsWs(storybookId)
      } catch (err) {
        setIsConnected(false)
        if (isAuthTicketError(err)) {
          console.warn('🔒 Notification WebSocket ticket refused — not reconnecting')
          return
        }
        console.error('Notification WebSocket ticket request failed:', err)
        scheduleReconnect()
        return
      }
      if (disposed) {
        socket.close(1000, 'Component unmounting')
        return
      }
      ws = socket

      ws.onopen = () => {
        console.log('🔔 Notification WebSocket connected')
        setIsConnected(true)
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          handleWebSocketMessage(message)
        } catch (err) {
          console.error('Failed to parse notification:', err)
        }
      }

      ws.onclose = (event) => {
        setIsConnected(false)
        // Auth rejections can't be fixed by retrying with the same credentials
        if (isAuthCloseCode(event.code)) {
          console.warn('🔒 Notification WebSocket rejected by auth — not reconnecting')
          return
        }
        scheduleReconnect()
      }

      ws.onerror = () => setIsConnected(false)
    }

    const handleWebSocketMessage = (message: any) => {
      switch (message.type) {
        case 'job:started':
          addNotification({
            type: 'info',
            category: 'batch_job',
            title: 'Batch Job Started',
            message: `Job ${message.payload?.jobId?.slice(0, 8)}... has started processing`,
            metadata: message.payload
          })
          break

        case 'job:completed':
          addNotification({
            type: 'success',
            category: 'batch_job',
            title: 'Batch Job Completed',
            message: `Job completed: ${message.payload?.completedRequests || 0} requests processed`,
            actionLabel: 'View Results',
            metadata: message.payload
          })
          break

        case 'job:failed':
          addNotification({
            type: 'error',
            category: 'batch_job',
            title: 'Batch Job Failed',
            message: message.payload?.error || 'An error occurred during processing',
            metadata: message.payload
          })
          break

        case 'job:retrying':
          addNotification({
            type: 'warning',
            category: 'batch_job',
            title: 'Batch Job Retrying',
            message: `Retry ${message.payload?.retryCount}/${message.payload?.maxRetries} scheduled`,
            metadata: message.payload
          })
          break
      }
    }

    void connect()

    return () => {
      disposed = true
      if (ws) ws.close(1000, 'Component unmounting')
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
    }
  }, [storybookId, addNotification])

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

